import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { TypedOrder } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { Card } from '../../../shared/components/ui/Card';

const ORDER_TYPE_LABELS: Record<string, string> = {
  'e-filing': 'eFiling',
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Document Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy Delivery',
  'secretary-of-state-filing': 'Secretary of State Filing',
  'county-recording': 'County Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
};

interface OrdersResponse {
  data: TypedOrder[];
  pagination: { page: number; per_page: number; total: number };
}

const CustomerDrafts: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const { get } = useApi();

  const [drafts, setDrafts] = useState<TypedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- loading flag before async fetch */
  useEffect(() => {
    setLoading(true);
    get<OrdersResponse>(`${API_BASE_URL}/api/orders?status=draft&perPage=50`)
      .then((res) => {
        setDrafts(res.data);
        setTotal(res.pagination.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [get]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // e-filing drafts resume in the wizard (server rehydration via ?orderId=);
  // other order types open the detail page where Edit Draft routes by type.
  const resumeDraft = (order: TypedOrder) => {
    if (order.type === 'e-filing') {
      navigate(`/eFiling?orderId=${order.id}`);
    } else {
      navigate(`/orders/${order.id}`);
    }
  };

  return (
    <div>
      <PageTitle>Draft Orders</PageTitle>

      {loading && (
        <p className="text-sm py-8 text-center" style={{ color: brand.colors.textLight }}>
          Loading drafts...
        </p>
      )}

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-red-500">Failed to load drafts: {error}</p>
        </Card>
      )}

      {!loading && !error && drafts.length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-xl font-light mb-2" style={{ color: brand.colors.textLight }}>
            You have no draft orders.
          </h3>
          <p className="text-sm" style={{ color: brand.colors.textMuted }}>
            Orders you save before submitting will be available here.
          </p>
        </div>
      )}

      {!loading && drafts.length > 0 && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              {total} draft{total !== 1 ? 's' : ''}
            </p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: brand.colors.border }}>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Order #</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Type</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Case</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Last Updated</th>
                <th className="text-right text-xs font-medium py-2" style={{ color: brand.colors.textLight }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((order) => (
                <tr
                  key={order.id}
                  className="border-b cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: brand.colors.border }}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="py-3 pr-4">
                    <span className="text-sm font-medium" style={{ color: brand.colors.link }}>
                      {order.referenceNumber}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {ORDER_TYPE_LABELS[order.type] ?? order.type}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {order.caseTitle || order.caseNumber || '—'}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.textLight }}>
                      {new Date(order.updatedAt ?? order.createdAt).toLocaleString()}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        resumeDraft(order);
                      }}
                      className="px-3 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      Edit Draft
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default CustomerDrafts;
